
export const getUserCodesFromAuthState = () => {
  const authState = window.__AUTH_STATE__;
  const userContext = authState?.userContext;
  const userAppStoreProfiles = userContext?.userInfo?.userAppStoreProfiles;

  return {
    businessTypeCodes: userAppStoreProfiles?.businessTypeCodes || [],
    productChannelCodes: userAppStoreProfiles?.productChannelCodes || [],
    isAuthenticated: authState?.authenticated || false
  };
};

/**
 * Get the legacy app base URL from environment variables
 */
export const getLegacyAppUrl = () => {
  return process.env.REACT_APP_LEGACY_APP_URL ||
         process.env.REACT_APP_LEGACY_PROD_APP_URL ||
         window.location.origin ||
         '';
};

/**
 * Get the current locale from window.__AUTH_STATE__
 * Defaults to 'en-US' if not available
 */
export const getLocale = () => {
  return window.__AUTH_STATE__?.locale || 'en-US';
};

const ABSOLUTE_HTTP_URL_REGEX = /^https?:\/\//i;
const trimTrailingSlashes = (value = '') => value.replace(/\/+$/, '');
const LEGACY_APP_PATH_REGEX = /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?apps(?:\/|$)/;

/**
 * Build a full URL with locale inserted after the domain
 * @param {string} path - The path to append (can be relative or absolute)
 * @returns {string} - The full URL with locale
 *
 * Examples:
 * - buildLegacyAppUrl('/apps/123') => 'https://apps.adp.com/en-US/apps/123'
 * - buildLegacyAppUrl('apps/123') => 'https://apps.adp.com/en-US/apps/123'
 * - buildLegacyAppUrl('http://example.com') => 'http://example.com' (absolute URLs returned as-is)
 */
export const buildLegacyAppUrl = (path) => {
  if (!path) return '';

  // If it's already an absolute URL, return as-is
  if (path.startsWith('http')) {
    return path;
  }

  const baseUrl = getLegacyAppUrl().replace(/\/$/, '');
  const locale = getLocale();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}/${locale}${normalizedPath}`;
};

export const buildLegacyLoginContinueUrl = (path, authenticated = !!window.__AUTH_STATE__?.authenticated) => {
  const legacyUrl = buildLegacyAppUrl(path);
  if (!legacyUrl || !authenticated || !ABSOLUTE_HTTP_URL_REGEX.test(legacyUrl)) return legacyUrl;

  try {
    const requestedUrl = new URL(legacyUrl);
    const legacyBase = new URL(getLegacyAppUrl());

    if (/^\/login(?:\/|$)/.test(requestedUrl.pathname) || /^\/logout(?:\/|$)/.test(requestedUrl.pathname)) {
      return legacyUrl;
    }

    if (!LEGACY_APP_PATH_REGEX.test(requestedUrl.pathname)) {
      return legacyUrl;
    }

    const legacyBasePath = trimTrailingSlashes(legacyBase.pathname);
    let relativePathname = requestedUrl.pathname;

    if (legacyBasePath && relativePathname.startsWith(legacyBasePath)) {
      relativePathname = relativePathname.slice(legacyBasePath.length) || '/';
    }

    if (!relativePathname.startsWith('/')) {
      relativePathname = `/${relativePathname}`;
    }

    const continuePath = `${relativePathname}${requestedUrl.search}${requestedUrl.hash}`;
    return `${legacyBase.origin}/login?continue=${encodeURIComponent(continuePath)}`;
  } catch {
    return legacyUrl;
  }
};

const DEFAULT_LISTING_PARAMS = 'start=0&numberOfResults=15&sort=relevancy';

const getStoredPlatformName = () => {
  try {
    const stored = localStorage.getItem('selectedPlatform');
    return JSON.parse(stored)?.displayName ?? '';
  } catch {
    return '';
  }
};

/**
 * Build a full listing URL by merging default params with facet params from the href fragment.
 * For unauthenticated users, appends f:PlatformFacet from localStorage selectedPlatform if present.
 * @param {string} href - The href containing /listing and an optional fragment (e.g. /listing?#f:SolutionsFacet=[...])
 * @returns {string} Full URL: baseUrl/locale/listing#start=0&numberOfResults=15&sort=relevancy[&facetParams]
 */
export const navigateToListingPage = (href) => {
  if (!href) return '';

  const { isAuthenticated } = getUserCodesFromAuthState();
  const hashIndex = href.indexOf('#');
  let fragment = hashIndex !== -1 ? href.slice(hashIndex + 1) : '';

  if (!isAuthenticated) {
    const platformName = getStoredPlatformName();
    if (platformName) {
      const platformFacet = `f:PlatformFacet=[${encodeURIComponent(platformName)}]`;
      fragment = fragment ? `${fragment}&${platformFacet}` : platformFacet;
    }
  }

  const listingPath = fragment
    ? `/listing#${DEFAULT_LISTING_PARAMS}&${fragment}`
    : `/listing#${DEFAULT_LISTING_PARAMS}`;

  return buildLegacyAppUrl(listingPath);
};

// ─── Search URL Builder ──────────────────────────────────────────────────────

const FACET_KEY = 'f:PlatformFacet';
const DEFAULT_HASH = '#start=0&numberOfResults=15&sort=relevancy&q=';

const resolveEffectivePlatformName = (hashSearchParams, storedPlatformName) => {
  const urlFacetName = hashSearchParams.get(FACET_KEY)?.replace(/[\[\]]/g, '').trim() ?? '';
  return (urlFacetName && urlFacetName !== storedPlatformName) ? urlFacetName : storedPlatformName;
};

const buildCorrectedHash = (hashParam, hasPlatformFacet, effectiveFacet) => {
  if (!hasPlatformFacet) return hashParam;
  return effectiveFacet
    ? hashParam.replace(/f:PlatformFacet=[^&#]*/g, effectiveFacet)
    : hashParam.replace(/[&]?f:PlatformFacet=[^&#]*/g, '');
};

const applyLayoutParam = (url, layoutParam) =>
  layoutParam ? `${url.replace(/&layout=[^&#]*/g, '')}&layout=${layoutParam}` : url;

const ensureQParam = (url) => /[&#]q=/.test(url) ? url : `${url}&q=`;

const appendFacetToHash = (hashValue, facetValue) => {
  if (!facetValue || hashValue.includes(`${FACET_KEY}=`)) return hashValue;
  return `${hashValue}${hashValue.includes('#') ? '&' : '#'}${facetValue}`;
};

/**
 * Builds the search URL for the listing page, handling:
 *  - Platform facet normalisation (strips accumulated brackets, prefers URL facet over localStorage)
 *  - Layout param preservation
 *  - Authenticated vs unauthenticated routing
 *
 * @param {object} options
 * @param {string}  options.searchValue       - Raw search term entered by the user
 * @param {string}  [options.hashParam='']    - Current hash string (e.g. "#start=0&…") passed by SearchBar
 * @param {boolean} [options.isAuthenticated=false]
 * @param {string}  [options.basePath='/listing'] - Base pathname of the search listing page
 * @returns {string} Full URL string ready for navigation (e.g. "/listing#start=0&…")
 */
export const buildSearchUrl = ({
  searchValue,
  hashParam = '',
  isAuthenticated = false,
  basePath = '/listing'
} = {}) => {
  const trimmedValue = searchValue?.trim() ?? '';

  const hashSearchParams = new URLSearchParams(hashParam.replace(/^#/, ''));
  const layoutParam = hashSearchParams.get('layout');

  const storedPlatformName = getStoredPlatformName();
  const effectiveName = resolveEffectivePlatformName(hashSearchParams, storedPlatformName);
  const effectiveFacet = effectiveName ? `${FACET_KEY}=[${encodeURIComponent(effectiveName)}]` : '';

  const hasPlatformFacet = hashParam.includes(`${FACET_KEY}=`);

  const baseHash = hashParam || DEFAULT_HASH;
  const freshHash = baseHash
    .replace(/([&#])q=[^&#]*/g, `$1q=${trimmedValue}`)
    .replace(/([&#])start=[^&#]*/g, '$1start=0');

  const correctedHash = buildCorrectedHash(freshHash, hasPlatformFacet, effectiveFacet);
  const normalizedHash = correctedHash || DEFAULT_HASH;
  const unauthenticatedHash = appendFacetToHash(normalizedHash, effectiveFacet);

  let targetUrl = basePath;

  if (isAuthenticated) {
    targetUrl += normalizedHash;
  } else {
    targetUrl += unauthenticatedHash;
  }

  return applyLayoutParam(ensureQParam(targetUrl), layoutParam);
};

export const slugify = (appName = '') =>
  appName
    .replace(/[®™©]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
